import { MediaItemDetailed, RelatedEntityItem } from "../types";

/**
 * Transforms relationship keys from the table format to the API format.
 *
 * The table uses tableName (e.g., "trips", "specialthings", "simpletags")
 * but the API expects relationshipKey (e.g., "trips", "special_things", "simple_tags").
 * This applies to both related_entities and geo_entities.
 *
 * @param relationships - The relationships object from the table
 * @param config - The application config containing API endpoints
 * @returns The transformed relationships object
 */
export const transformRelationships = (
  relationships: MediaItemDetailed["relationships"],
  config: any
): MediaItemDetailed["relationships"] => {
  if (!relationships) {
    return { related_entities: {}, geo_entities: {}, media: [] };
  }

  // Map tableName to relationshipKey for API writes (V2: tableName is the canonical key)
  const returnedToRelationshipKeyMap = config.apiEndpoints.reduce(
    (map: Record<string, string>, endpoint: any) => {
      if (endpoint.tableName && endpoint.relationshipKey) {
        map[endpoint.tableName] = endpoint.relationshipKey;
      }
      return map;
    },
    {} as Record<string, string>
  );

  // Transform both related_entities and geo_entities keys
  const result = {
    related_entities: {} as Record<string, any[]>,
    geo_entities: {} as Record<string, any[]>,
    media: [] as any[]
  };

  // Transform related_entities
  if (relationships.related_entities) {
    for (const [returnedKey, entities] of Object.entries(relationships.related_entities)) {
      // Find the correct API key for this entity type
      const relationshipKey = returnedToRelationshipKeyMap[returnedKey] || returnedKey;

      // Create cleaned entity arrays with only the fields the backend needs
      const cleanedEntities = entities.map((entity: RelatedEntityItem) => ({
        id: Number(entity.id),
        rel_description: entity.rel_description || "Associated through editor",
        is_primary: Boolean(entity.is_primary)
      }));

      result.related_entities[relationshipKey] = cleanedEntities;
    }
  }

  // Transform geo_entities
  if (relationships.geo_entities) {
    for (const [returnedKey, entities] of Object.entries(relationships.geo_entities)) {
      // Find the correct API key for this entity type
      const relationshipKey = returnedToRelationshipKeyMap[returnedKey] || returnedKey;

      // Create cleaned entity arrays with only the fields the backend needs
      const cleanedEntities = entities.map((entity: RelatedEntityItem) => ({
        id: Number(entity.id),
        rel_description: entity.rel_description || "Associated through editor",
        is_primary: Boolean(entity.is_primary)
      }));

      result.geo_entities[relationshipKey] = cleanedEntities;
    }
  }

  // Transform media relationships
  if (relationships.media && relationships.media.length > 0) {
    result.media = relationships.media.map((entity: RelatedEntityItem) => ({
      id: Number(entity.id),
      rel_description: entity.rel_description || "Associated through editor",
      is_primary: Boolean(entity.is_primary)
    }));
  }

  return result;
};
